import React, { useState, useEffect, useMemo } from "react";
import { useCityStore } from "../../store/useCityStore";
import type { City } from "../../types/city";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { CityFilters } from "./CityFilters";
import { CitiesTable } from "./CitiesTable";
import { CityPagination } from "./CityPagination";
import { CityModal } from "./CityModal";
import { Plus, RefreshCw } from "lucide-react";

export const CitiesManagement: React.FC = () => {
  const {
    cities,
    loading,
    isRefreshing,
    loadIfNeeded,
    addCity,
    updateCity,
    removeCity,
    refreshCities,
  } = useCityStore();

  // Local state for filters, pagination, and UI
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [countryCodeFilter, setCountryCodeFilter] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("city_name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "delete">(
    "create",
  );
  const [selectedCity, setSelectedCity] = useState<City | null>(null);

  const itemsPerPage = 10;

  useEffect(() => {
    loadIfNeeded().catch(console.error);
  }, [loadIfNeeded]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, countryCodeFilter, sortBy, sortOrder]);

  const filteredCities = useMemo(() => {
    const query = search.trim().toLowerCase();
    const next = cities.filter((city) => {
      if (query) {
        const searchable =
          `${city.city_id} ${city.city_name} ${city.country_code}`.toLowerCase();
        if (!searchable.includes(query)) {
          return false;
        }
      }
      if (countryCodeFilter && city.country_code !== countryCodeFilter) {
        return false;
      }
      return true;
    });

    next.sort((a, b) => {
      const getSortValue = (city: City): string => {
        if (sortBy === "country_code") {
          return city.country_code;
        }
        if (sortBy === "city_id") {
          return city.city_id;
        }
        return city.city_name;
      };

      const aVal = getSortValue(a);
      const bVal = getSortValue(b);
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return next;
  }, [cities, search, countryCodeFilter, sortBy, sortOrder]);

  const totalCount = filteredCities.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const paginatedCities = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCities.slice(start, start + itemsPerPage);
  }, [filteredCities, currentPage, itemsPerPage]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle sort change
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  // Handle create city
  const handleCreate = () => {
    setSelectedCity(null);
    setModalMode("create");
    setModalOpen(true);
  };

  // Handle edit city
  const handleEdit = (city: City) => {
    setSelectedCity(city);
    setModalMode("edit");
    setModalOpen(true);
  };

  // Handle delete city
  const handleDelete = (city: City) => {
    setSelectedCity(city);
    setModalMode("delete");
    setModalOpen(true);
  };

  // Handle save (create or update)
  const handleSave = async (cityData: Record<string, unknown>) => {
    if (modalMode === "create") {
      await addCity(cityData as Omit<City, "created_at" | "updated_at">);
    } else if (modalMode === "edit" && selectedCity) {
      await updateCity(
        selectedCity.city_id,
        cityData as Partial<Pick<City, "city_name" | "country_code">>,
      );
    } else if (modalMode === "delete" && selectedCity) {
      await removeCity(selectedCity.city_id);
    }

    setModalOpen(false);
  };

  // Handle refresh
  const handleRefresh = () => {
    refreshCities().catch(console.error);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Cities</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage cities for campaign assignments
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRefresh}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center space-x-2"
            disabled={isRefreshing}
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add City
          </button>
        </div>
      </div>

      {/* Filters */}
      <CityFilters
        search={search}
        onSearchChange={setSearch}
        countryCode={countryCodeFilter}
        onCountryCodeChange={setCountryCodeFilter}
      />

      {/* Table */}
      {loading && cities.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <CitiesTable
            cities={paginatedCities}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <CityPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Modal */}
      <CityModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        city={selectedCity}
        onSave={handleSave}
      />
    </div>
  );
};
